import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SigningTokenInfo } from '@corner/shared';
import { recipientViewFields } from '../../lib/signingRecipientView';

const API_BASE = import.meta.env.VITE_API_URL || '';
const F = 'Geist, system-ui, sans-serif';
const SERIF = '"Instrument Serif", system-ui, sans-serif';

export default function SigningCompletePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<SigningTokenInfo | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadAvailable, setDownloadAvailable] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/signing/token/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.code === 'TOKEN_EXPIRED') {
            navigate(`/sign/${token}/error?type=expired`, { replace: true });
          } else if (data.code === 'TOKEN_REVOKED') {
            navigate(`/sign/${token}/error?type=revoked`, { replace: true });
          } else {
            navigate(`/sign/${token}/error?type=not_found`, { replace: true });
          }
          return null;
        }
        return res.json();
      })
      .then((data: SigningTokenInfo | null) => {
        if (!data) return;
        setInfo(data);
        // Check if all parties signed (download only available then)
        if (data.status === 'completed') {
          setDownloadAvailable(true);
        }
      })
      .catch(() => {
        navigate(`/sign/${token}/error?type=not_found`, { replace: true });
      });
  }, [token, navigate]);

  const yourFields = useMemo(
    () => recipientViewFields(info?.fields, info?.pageCount),
    [info?.fields, info?.pageCount],
  );

  const handleDownload = async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/download`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = info ? `signed_${info.documentName}` : 'signed_document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!token || !info) return;
    setDownloadingDocx(true);
    try {
      const res = await fetch(`${API_BASE}/api/signing/token/${token}/download-docx`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = info.documentName.replace(/\.(pdf|docx)$/i, '').trim() || 'document';
      a.download = `signed_${base}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingDocx(false);
    }
  };

  if (!info) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F2ED', fontFamily: F }}>
        <p style={{ margin: 0, fontSize: 14, color: '#9E9189' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#F5F2ED', fontFamily: F }}>
      {/* Top bar */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #E8DED4', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <svg width="22" height="22" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
              <defs><linearGradient id="cLogoC" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse"><stop stopColor="#D46A3A"/><stop offset="1" stopColor="#8B3A10"/></linearGradient></defs>
              <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#cLogoC)" strokeWidth="40" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#3D3229' }}>Corner</span>
          </div>
          <div style={{ width: 1, height: 20, backgroundColor: '#E8DED4', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#6B5E52' }}>Complete</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="11" height="11" viewBox="0 0 10 10" fill="none">
            <path d="M5 1L2 2.5v2.5c0 2 1.3 3.5 3 4 1.7-.5 3-2 3-4V2.5L5 1z" stroke="#1B7A4B" strokeWidth="0.8" />
          </svg>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#1B7A4B' }}>Secure</span>
        </div>
      </div>

      {/* Centered card */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          width: 480, backgroundColor: '#FFFFFF', borderRadius: 16,
          padding: '48px 40px 40px', boxShadow: '0px 4px 24px #0000000F',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
        }}>
          {/* Success icon */}
          <div style={{
            width: 64, height: 64, borderRadius: 32, backgroundColor: '#E8F5ED',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M6 14l6 6L22 8" stroke="#1B7A4B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Text */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 400, color: '#3D3229', fontFamily: SERIF }}>
              Document signed
            </h1>
            <p style={{ margin: 0, fontSize: 14, color: '#6B5E52', textAlign: 'center', lineHeight: '21px' }}>
              {downloadAvailable
                ? `All required signatures have been collected for ${info.documentName}.`
                : `Your signature has been applied. ${info.senderName} will be notified when all parties have signed.`}
            </p>
          </div>

          {/* Field summary */}
          {yourFields.length > 0 && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {yourFields.map((f, i) => (
                <div
                  key={f.id ?? i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 8, backgroundColor: '#F4FAF6',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: 10, backgroundColor: '#1B7A4B',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.5 2.5L8 3" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#3D3229' }}>{f.label}</span>
                    <span style={{ fontSize: 11, color: '#9E9189' }}>
                      Page {f.page}{f.signedAt ? ` · ${new Date(f.signedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ' · Signed'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {downloadAvailable && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  width: '100%', border: 'none', borderRadius: 8, padding: '13px 20px',
                  backgroundColor: '#2E2318', color: '#FDFCFB',
                  fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 2v7m0 0L4.5 6.5M7 9l2.5-2.5M2 11h10" stroke="#FDFCFB" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {downloading ? 'Downloading...' : 'Download Signed Copy'}
              </button>
            )}
            {downloadAvailable && (
              <>
                <button
                  type="button"
                  onClick={handleDownloadDocx}
                  disabled={downloadingDocx}
                  style={{
                    width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 20px',
                    backgroundColor: '#FAFAF8', color: '#3D3229',
                    fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {downloadingDocx ? 'Preparing Word…' : 'Download Word (.docx)'}
                </button>
                <p style={{ margin: '0 0 4px', fontSize: 11, lineHeight: '16px', color: '#9E9189', textAlign: 'center' }}>
                  Editable text from the signed PDF — formatting may differ from the PDF.
                </p>
              </>
            )}

            <button
              onClick={() => { window.open('/', '_blank'); }}
              style={{
                width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 20px',
                backgroundColor: 'transparent', color: '#3D3229',
                fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              Learn more about Corner
            </button>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, paddingTop: 4 }}>
            {downloadAvailable && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 16, height: 16, borderRadius: 8, backgroundColor: '#1B7A4B',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4l1.5 1.5L6.5 2.5" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span style={{ fontSize: 12, fontWeight: 400, color: '#3D3229' }}>All parties have signed</span>
              </div>
            )}
            <span style={{ fontSize: 12, fontWeight: 400, color: '#B5AA9C' }}>
              You can safely close this window
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
